import type {
  OrderProductItem,
  Payment_Information,
} from '../screens/checkout/components/Receipt';
import { roundMoney, roundToNumericPrecision } from '../utils/libs';
import { saleLineUnitPrice } from '../utils/backofficeSaleLines';

/** This payment's row on the 'print-tx' slip, whatever the method. */
export const AMOUNT_RECEIVED_LABEL = 'Amount Received';

export type ReceiptTotalsRow = {
  label: string;
  amount: number;
  strong?: boolean;
  emphasis?: 'balance' | 'change';
};

export type ReceiptTotalsSummary = {
  rows: ReceiptTotalsRow[];
  grandTotal: number;
  isPaidInFull: boolean;
  isPartialLedger: boolean;
  hasChange: boolean;
};

/**
 * Render mode:
 *   'preview' — on-screen checkout preview (before or during a payment). We must
 *     NOT invent a settled ledger from partial data; specifically, we must not
 *     conclude "paid in full" just because the backend has not yet returned a
 *     `bp_payment_amount_due` field.
 *   'print'  — order-ledger slip (Order Details reprint). Cumulative Total Paid
 *     and lifetime refunds / PAID IN FULL stamp are allowed.
 *   'print-tx' — this-transaction slip (PaymentModal Print/Share). Matches the
 *     modal: current bill, cash received, latest change, remaining balance.
 */
export type ReceiptTotalsMode = 'preview' | 'print' | 'print-tx';

/** Unpaid part of a sale line: mass at measure precision, or item count. */
export function unpaidLineQuantity(item: OrderProductItem): number {
  return item.bp_product_information?.bp_product_price_type === 'unit'
    ? roundToNumericPrecision(
        (item.bp_order_mass ?? 0) - (item.bp_order_paid_mass ?? 0),
      )
    : (item.bp_order_quantity ?? 0) - (item.bp_order_paid_quantity ?? 0);
}

/**
 * Amount owed on the unpaid part of a line, priced and rounded the way the
 * server does it: unit rate × quantity, rounded once at money precision.
 * Partial checkout lines carry the rate captured before the selection
 * overwrote their quantity (bp_line_unit_price); use it when present.
 */
export function unpaidLineAmount(item: OrderProductItem): number {
  const rate = item.bp_line_unit_price ?? saleLineUnitPrice(item);
  return roundMoney(rate * unpaidLineQuantity(item));
}

/** Sum of unpaidLineAmount over the order, at money precision. */
export function unpaidLinesTotal(order: OrderProductItem[]): number {
  return roundMoney(
    order.reduce(
      (sum, item) =>
        typeof item === 'object' && item?.bp_product_information
          ? sum + unpaidLineAmount(item)
          : sum,
      0,
    ),
  );
}

/**
 * Compute the cumulative amount paid on the order. This is the SAME derivation
 * the Order Details screen uses (see `screens/orders/OrderDetails.tsx`
 * `calculatedTotalAmountPaid`) so that the "Total Paid" figure on a printed
 * receipt matches what the user sees on the order-detail page.
 *
 * Priority:
 *   1. Sum of `bp_payments[].amount` when the array is present (authoritative).
 *   2. `bp_payment_amount_received` scalar (backend mirror).
 *   3. `bp_payment_already_paid_amount` scalar (legacy fallback).
 */
function computeCumulativePaid(pi: Payment_Information | undefined): number {
  const payments = pi?.bp_payments;
  if (payments && payments.length > 0) {
    return roundMoney(
      payments.reduce(
        (acc, p) => acc + Math.max(0, Number(p?.amount ?? 0)),
        0,
      ),
    );
  }
  if (pi?.bp_payment_amount_received != null) {
    return Math.max(0, Number(pi.bp_payment_amount_received));
  }
  return Math.max(0, Number(pi?.bp_payment_already_paid_amount ?? 0));
}

/**
 * Change for THIS payment only — same rules as PaymentModal.
 * Do not sum lifetime refunds (that breaks 2nd+ partial pays).
 */
function getLatestChangeAmount(pi: Payment_Information | undefined): number {
  const refunds = pi?.bp_payment_refunds;
  const payments = pi?.bp_payments;
  const latestPayment =
    payments && payments.length > 0 ? payments[payments.length - 1] : null;
  const latestPaymentId = latestPayment?._id;
  const latestRefund =
    refunds && refunds.length > 0 ? refunds[refunds.length - 1] : null;
  const latestRefundAmount = latestRefund?.amount;
  const refundPaymentId = latestRefund?.payment_id;

  const shouldShowRefund =
    latestRefundAmount !== null &&
    latestRefundAmount !== undefined &&
    Number(latestRefundAmount) >= 0 &&
    (refundPaymentId == null ||
      (latestPaymentId != null &&
        String(refundPaymentId) === String(latestPaymentId)));

  if (shouldShowRefund) {
    return Math.max(0, Number(latestRefundAmount));
  }
  // No this-tx refund match. Only use scalar when there is no refunds array
  // (legacy single-payment payloads). Never use the scalar as a lifetime sum
  // when refunds[] exists — that double-counts prior partial pays.
  if (!refunds || refunds.length === 0) {
    return Math.max(0, Number(pi?.bp_payment_amount_refunded ?? 0));
  }
  return 0;
}

/**
 * Build a totals block matching Kenyan retail receipt conventions
 * (Naivas / Quickmart / M-Pesa till slip / duka layaway norms).
 *
 * States rendered (print mode):
 *   • Full cash sale, no change            → Order Total, Total Paid.
 *   • Full cash sale WITH change           → Order Total, Total Paid, Change Given.
 *   • Partial payment (deposit / installment) → Order Total, Total Paid, Change Given, Balance Due.
 *   • Final payment closing an order       → same as partial, Balance Due omitted;
 *                                            renderer stamps PAID IN FULL.
 *
 *   `Total Paid` is the SAME figure the Order Details screen calls
 *   "Amount Paid" (sum of bp_payments[].amount). We deliberately do not print
 *   a separate "Amount Paid" row: it duplicates "Total Paid" and previously
 *   showed a misleading per-tx figure.
 *
 * States rendered (preview mode):
 *   • No prior payments → no rows (grand Total only).
 *   • Prior payments exist + backend confirmed bp_payment_amount_due →
 *     Order Total, Already Paid, Balance Due rows. The bold "Total (KSH)"
 *     shows the confirmed balance.
 *   • Prior payments exist + bp_payment_amount_due not yet returned →
 *     Order Total, Already Paid rows only. The bold "Total (KSH)" falls back
 *     to fullOrderTotal (the selected-items subtotal in the partial-checkout
 *     flow), matching the Confirm Payment button. This avoids double-counting
 *     alreadyPaid, which in PartialOrdersCheckoutScreen is the order's
 *     cumulative prior payment for OTHER items, not a subset of the current
 *     transaction.
 *   • Never claims paid-in-full and never renders Change,
 *     because those are transaction outcomes that only exist AFTER the backend
 *     responds.
 */
export function buildReceiptTotalsSummary(
  pi: Payment_Information | undefined,
  fallbackOrderTotalRaw: number,
  mode: ReceiptTotalsMode = 'print',
): ReceiptTotalsSummary {
  const fallbackOrderTotal = roundMoney(fallbackOrderTotalRaw);
  const orderTotal = Number(
    pi?.bp_current_bill_amount ??
      pi?.bp_payment_amount ??
      fallbackOrderTotal ??
      0,
  );
  const previouslyPaid = Math.max(
    0,
    Number(pi?.bp_payment_already_paid_amount ?? 0),
  );
  const isPartialLedger = previouslyPaid > 0;

  // Preview: surface what the backend has confirmed plus a derived Balance Due
  // so the customer doesn't have to subtract Order Total − Already Paid in
  // their head. The bold "Total (KSH)" line shows what is still owed, not the
  // original order total.
  if (mode === 'preview') {
    const rows: ReceiptTotalsRow[] = [];

    const fullOrderTotal = Number(pi?.bp_payment_amount ?? 0);
    const dueIsKnown = pi?.bp_payment_amount_due != null;
    const balance = dueIsKnown
      ? Math.max(0, Number(pi!.bp_payment_amount_due))
      : 0;

    // Partial selection: user picked a subset of items to pay now.
    // fullOrderTotal (bp_payment_amount) is the whole order, fallbackOrderTotal
    // is the selected-items subtotal.
    const isPartialSelection =
      fullOrderTotal > 0 &&
      fallbackOrderTotal > 0 &&
      fullOrderTotal !== fallbackOrderTotal;

    if (isPartialSelection) {
      rows.push({ label: 'Order Total', amount: fullOrderTotal });
      if (previouslyPaid > 0) {
        rows.push({ label: 'Already Paid', amount: previouslyPaid });
      }
      const balanceDue = roundMoney(
        fullOrderTotal - previouslyPaid - fallbackOrderTotal,
      );
      if (balanceDue > 0) {
        rows.push({
          label: 'Balance Due',
          amount: balanceDue,
          strong: true,
          emphasis: 'balance',
        });
      }
    } else if (previouslyPaid > 0) {
      rows.push({ label: 'Order Total', amount: fullOrderTotal || orderTotal });
      rows.push({ label: 'Already Paid', amount: previouslyPaid });
      if (dueIsKnown && balance > 0) {
        rows.push({
          label: 'Balance Due',
          amount: balance,
          strong: true,
          emphasis: 'balance',
        });
      }
    }

    return {
      rows,
      grandTotal: isPartialSelection
        ? fallbackOrderTotal
        : dueIsKnown && previouslyPaid > 0
          ? balance
          : previouslyPaid > 0
            ? fallbackOrderTotal
            : fullOrderTotal || fallbackOrderTotal || 0,
      isPaidInFull: false,
      isPartialLedger,
      hasChange: false,
    };
  }

  // This-transaction slip (PaymentModal Print/Share). Match modal fields —
  // never sum lifetime payments/refunds.
  if (mode === 'print-tx') {
    const fullOrder = Number(pi?.bp_payment_amount ?? 0);
    const currentBill = Number(
      pi?.bp_current_bill_amount ?? fallbackOrderTotal ?? 0,
    );
    const cashReceived =
      pi?.bp_amount_paid != null ? Math.max(0, Number(pi.bp_amount_paid)) : null;
    const change = getLatestChangeAmount(pi);
    const dueIsKnown = pi?.bp_payment_amount_due != null;
    const balance = dueIsKnown
      ? Math.max(0, Number(pi!.bp_payment_amount_due))
      : 0;
    const hasSplit =
      fullOrder > 0 && currentBill > 0 && fullOrder !== currentBill;

    const rows: ReceiptTotalsRow[] = [];
    if (hasSplit) {
      rows.push({ label: 'Order Total', amount: fullOrder, strong: true });
      rows.push({ label: 'Sub-Total', amount: currentBill });
    } else {
      rows.push({
        label: 'Order Total',
        amount: currentBill || fullOrder || fallbackOrderTotal || 0,
      });
    }
    if (cashReceived != null && cashReceived > 0) {
      rows.push({ label: AMOUNT_RECEIVED_LABEL, amount: cashReceived });
    }
    if (change > 0) {
      rows.push({
        label: 'Change Given',
        amount: change,
        strong: true,
        emphasis: 'change',
      });
    }
    if (dueIsKnown && balance > 0) {
      rows.push({
        label: 'Balance Due',
        amount: balance,
        strong: true,
        emphasis: 'balance',
      });
    }

    const paymentsCount = pi?.bp_payments?.length ?? 0;
    return {
      rows,
      grandTotal: currentBill || fullOrder || fallbackOrderTotal || 0,
      isPaidInFull: dueIsKnown && balance === 0 && paymentsCount > 0,
      isPartialLedger: previouslyPaid > 0 || hasSplit,
      hasChange: change > 0,
    };
  }

  // Change/refund: sum ALL refunds when the array is present (matches
  // OrderDetails `calculatedTotalRefunds`), else fall back to the scalar.
  const refundsArray = pi?.bp_payment_refunds ?? [];
  const totalRefunds = refundsArray.length > 0
    ? roundMoney(
        refundsArray.reduce((acc, r) => acc + Number(r.amount ?? 0), 0),
      )
    : 0;
  const changeFromScalar = Number(pi?.bp_payment_amount_refunded ?? 0);
  const change = totalRefunds > 0 ? totalRefunds : Math.max(0, changeFromScalar);

  // Balance: only trust bp_payment_amount_due when it is explicitly present.
  // Never coerce a missing field into "balance = 0".
  const dueIsKnown = pi?.bp_payment_amount_due != null;
  const balance = dueIsKnown ? Math.max(0, Number(pi!.bp_payment_amount_due)) : 0;

  // Cumulative amount paid on the order to date. Matches OrderDetails "Amount Paid".
  const cumulativePaid = computeCumulativePaid(pi);
  const paymentsCount = pi?.bp_payments?.length ?? 0;

  // Partial installment slip: `bp_current_bill_amount` is what this transaction
  // settles (e.g. 200), `bp_payment_amount` is the full order total (e.g. 1400).
  // When they differ, print BOTH so the receipt is not a mystery ledger where
  // Sub-Total (200) + Balance (1200) ≠ any figure the customer can see.
  const currentBillRaw = pi?.bp_current_bill_amount;
  const paymentAmountRaw = pi?.bp_payment_amount;
  const hasInstallmentSplit =
    currentBillRaw != null &&
    paymentAmountRaw != null &&
    Number(currentBillRaw) !== Number(paymentAmountRaw);
  const fullOrderTotal = hasInstallmentSplit
    ? Number(paymentAmountRaw)
    : orderTotal;

  // Partial item selection (PartialOrdersCheckout): full order in
  // bp_payment_amount, selected subtotal in fallbackOrderTotal. Mirror preview
  // so thermal/HTML match the on-screen receipt. Skip when backend already
  // sent an installment split via bp_current_bill_amount.
  const isPartialSelection =
    !hasInstallmentSplit &&
    Number(paymentAmountRaw ?? 0) > 0 &&
    fallbackOrderTotal > 0 &&
    Number(paymentAmountRaw) !== fallbackOrderTotal;

  if (isPartialSelection) {
    const full = Number(paymentAmountRaw);
    const rows: ReceiptTotalsRow[] = [
      { label: 'Order Total', amount: full },
    ];
    if (previouslyPaid > 0) {
      rows.push({ label: 'Already Paid', amount: previouslyPaid });
    }
    const balanceDue = roundMoney(full - previouslyPaid - fallbackOrderTotal);
    if (balanceDue > 0) {
      rows.push({
        label: 'Balance Due',
        amount: balanceDue,
        strong: true,
        emphasis: 'balance',
      });
    }
    return {
      rows,
      grandTotal: fallbackOrderTotal,
      isPaidInFull: false,
      isPartialLedger,
      hasChange: false,
    };
  }

  const rows: ReceiptTotalsRow[] = [];

  if (hasInstallmentSplit) {
    rows.push({ label: 'Order Total', amount: fullOrderTotal, strong: true });
  }

  rows.push({
    label: hasInstallmentSplit ? 'Sub-Total' : 'Order Total',
    amount: orderTotal,
  });

  if (cumulativePaid > 0) {
    rows.push({ label: 'Total Paid', amount: cumulativePaid, strong: true });
  }

  if (change > 0) {
    rows.push({
      label: 'Change Given',
      amount: change,
      strong: true,
      emphasis: 'change',
    });
  }

  if (dueIsKnown && balance > 0 && cumulativePaid > 0) {
    rows.push({
      label: 'Balance Due',
      amount: balance,
      strong: true,
      emphasis: 'balance',
    });
  }

  return {
    rows,
    // Bottom "Total" line reflects the full order total on an installment
    // slip, otherwise the current bill (unchanged for full cash sales).
    grandTotal: fullOrderTotal || orderTotal || fallbackOrderTotal || 0,
    // PAID IN FULL requires: backend confirmed balance is exactly 0,
    // a payment has actually been recorded, AND we are on a partial-ledger
    // flow (single-shot cash sales don't get stamped).
    isPaidInFull:
      dueIsKnown && balance === 0 && paymentsCount > 0 && isPartialLedger,
    isPartialLedger,
    hasChange: change > 0,
  };
}
